import { useQuery } from "@apollo/client";
import { getDivisionAndMillLocations } from "api/queries/locationsAPI";
import {
    IGetSummaryReportDashboardFilters,
    ILocationsEntity,
    IDetailsFilters,
    IMillLocation,
    IGetSummaryReportFilters,
    ICorporateDashboardFiltersData,
    IDetailsFiltersQuery,
    IGetSummaryReportDashboardFiltersQuery,
    ICorporateDashboardFiltersDataQuery,
    IDetailsShiftFiltersQuery,
} from "./LocationsEntityInterfaces";
import {
    getCorporateDashboardFilters,
    getDetailsFilters,
    getSummaryReportDashboardFilters,
    getSummaryReportFilters,
} from "api/queries/filtersAPI";
import { useEffect, useMemo, useState } from "react";
import { IDetailsReportFilters } from "pages/Corporate/Locations/MillDashboards/DetailsReport/DetailsReportFilters/DetailsFiltersInterfaces";
import { END_OF_SHIFT_FAILURE_CODE_NAME, MEASURE_METRICS, allElements } from "constants/constants";
import { getEndOfDateRange, getStartOfDateRange } from "utilities/utilities";
import { ISummaryReportDashboardFilters } from "pages/Corporate/Locations/MillDashboards/SummaryReportDashboard/components/SummaryReportDashboardFilters/SummaryReportDashboardFiltersInterfaces";
import { ICorporateDashboardFilters } from "pages/Corporate/CorporateDashboard/useCorporateDashboardFilters";

export const LocationsEntity: ILocationsEntity = {
    GetMillLocations: (mill_id: number) => {
        const { loading, error, data } = useQuery(getDivisionAndMillLocations, { variables: { mill_id } });
        const [mill, setMill] = useState<IMillLocation>({} as IMillLocation);

        useEffect(() => {
            if (data) {
                setMill(data.mill[0]);
            }
        }, [data]);

        return { loading, mill, error };
    },
    GetSummaryReportFilters: (mill_id: number) => {
        const { loading, error, data } = useQuery(getSummaryReportFilters, {
            fetchPolicy: "no-cache",
            variables: { mill_id },
        });

        const [summaryReportFiltersData, setSummaryReportFiltersData] = useState<IGetSummaryReportFilters>(
            {} as IGetSummaryReportFilters,
        );

        useEffect(() => {
            if (data) {
                setSummaryReportFiltersData(data);
            }
        }, [data]);
        return { loading, summaryReportFiltersData, error };
    },
    GetSummaryReportDashboardFilters: (mill_id: number, filters: ISummaryReportDashboardFilters) => {
        const createdAtFilter = {
            created_at: {
                _gte: getStartOfDateRange(filters.startDate as Date),
                _lte: getEndOfDateRange(filters.endDate as Date),
            },
        };
        const noEndOfShiftFilter = {
            _or: [
                {
                    failure_code: {
                        _or: [
                            {
                                code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                            },
                            {
                                code: { _is_null: true },
                            },
                        ],
                    },
                },
                {
                    failure_code_id: { _is_null: true },
                },
            ],
        };
        const productionLineFilter = {
            production_line_id: {
                _eq: filters.productionLine,
            },
        };
        const { loading, error, data } = useQuery(getSummaryReportDashboardFilters, {
            variables: {
                whereSummaryFilters: {
                    ...noEndOfShiftFilter,
                    ...createdAtFilter,
                    shift: { shift_id: { _is_null: false } },
                    machine_center: {
                        production_line: {
                            ...(filters.productionLine !== allElements && productionLineFilter),
                            mill_id: { _eq: mill_id },
                        },
                    },
                    measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                },
                whereSummaryFunctionalLocationFilters: {
                    ...noEndOfShiftFilter,
                    ...createdAtFilter,
                    shift: { shift_id: { _is_null: false } },
                    machine_center: {
                        production_line: {
                            ...(filters.productionLine !== allElements && productionLineFilter),
                            mill_id: { _eq: mill_id },
                        },
                    },
                    measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                },
            },
        });

        const queryDataSummaryFilters: IGetSummaryReportDashboardFiltersQuery[] = useMemo(
            () => data?.summary_filters || [],
            [data?.summary_filters],
        );
        const queryDataSummaryFunctionalLocationFilters: IGetSummaryReportDashboardFiltersQuery[] = useMemo(
            () => data?.summary_functional_location_filters || [],
            [data?.summary_functional_location_filters],
        );

        const filtersData = useMemo(() => {
            const summaryFilters: IGetSummaryReportDashboardFilters = {
                failure_type: (filters.functionalLocation !== allElements
                    ? queryDataSummaryFunctionalLocationFilters
                    : queryDataSummaryFilters
                ).reduce((acc: IGetSummaryReportDashboardFilters["failure_type"], obj) => {
                    const failureTypeId = obj.failure_type.failure_type_id;
                    const existingFailureTypeIndex = acc.findIndex(
                        (summary) => summary.failure_type_id === failureTypeId,
                    );

                    if (!(existingFailureTypeIndex >= 0)) {
                        acc = [
                            ...acc,
                            {
                                failure_type_id: obj.failure_type.failure_type_id,
                                default_failure_type_translations: obj.failure_type.default_failure_type_translations,
                                name: obj.failure_type.name,
                                color_code: obj.failure_type.color_code,
                                is_location_specific: obj.failure_type.is_location_specific,
                                failure_codes: obj.failure_type.failure_codes,
                                machine_centers: [
                                    {
                                        machine_center_id: obj.machine_center.machine_center_id,
                                    },
                                ],
                                functional_locations: obj.functional_location
                                    ? [{ functional_location_id: obj.functional_location.functional_location_id }]
                                    : [],
                                shifts: [{ shift_id: obj.shift.shift_id }],
                            },
                        ];
                    } else {
                        const existingMachineCenterIndex = acc[existingFailureTypeIndex].machine_centers.findIndex(
                            (summary) => summary.machine_center_id === obj.machine_center.machine_center_id,
                        );
                        const existingFunctionalLocationIndex = acc[
                            existingFailureTypeIndex
                        ].functional_locations.findIndex(
                            (summary) =>
                                summary.functional_location_id === obj.functional_location?.functional_location_id,
                        );
                        const existingShiftIndex = acc[existingFailureTypeIndex].shifts.findIndex(
                            (summary) => summary.shift_id === obj.shift.shift_id,
                        );

                        if (!(existingMachineCenterIndex >= 0)) {
                            acc[existingFailureTypeIndex] = {
                                ...acc[existingFailureTypeIndex],
                                machine_centers: [
                                    ...acc[existingFailureTypeIndex].machine_centers,
                                    {
                                        machine_center_id: obj.machine_center.machine_center_id,
                                    },
                                ],
                            };
                        }
                        if (obj.functional_location && !(existingFunctionalLocationIndex >= 0)) {
                            acc[existingFailureTypeIndex] = {
                                ...acc[existingFailureTypeIndex],
                                functional_locations: [
                                    ...acc[existingFailureTypeIndex].functional_locations,
                                    { functional_location_id: obj.functional_location.functional_location_id },
                                ],
                            };
                        }
                        if (!(existingShiftIndex >= 0)) {
                            acc[existingFailureTypeIndex] = {
                                ...acc[existingFailureTypeIndex],
                                shifts: [...acc[existingFailureTypeIndex].shifts, { shift_id: obj.shift.shift_id }],
                            };
                        }
                    }
                    return acc;
                }, []),
                machine_center: queryDataSummaryFilters.reduce(
                    (acc: IGetSummaryReportDashboardFilters["machine_center"], obj) => {
                        const machineCenterId = obj.machine_center.machine_center_id;
                        const existingMachineCenterIndex = acc.findIndex(
                            (summary) => summary.machine_center_id === machineCenterId,
                        );

                        if (!(existingMachineCenterIndex >= 0)) {
                            acc = [
                                ...acc,
                                {
                                    machine_center_id: obj.machine_center.machine_center_id,
                                    name: obj.machine_center.name,
                                },
                            ];
                        }
                        return acc;
                    },
                    [],
                ),
                functional_location: queryDataSummaryFunctionalLocationFilters.reduce(
                    (acc: IGetSummaryReportDashboardFilters["functional_location"], obj) => {
                        const functionalLocationId = obj?.functional_location?.functional_location_id;
                        const existingFunctionalLocationIndex = acc.findIndex(
                            (summary) => summary.functional_location_id === functionalLocationId,
                        );

                        if (obj?.functional_location && !(existingFunctionalLocationIndex >= 0)) {
                            acc = [
                                ...acc,
                                {
                                    translation: obj?.functional_location.translation,
                                    functional_location_id: obj.functional_location?.functional_location_id,
                                    area: {
                                        machine_center_id: obj.machine_center.machine_center_id,
                                    },
                                },
                            ];
                        }
                        return acc;
                    },
                    [],
                ),
                shift: (filters.functionalLocation !== allElements
                    ? queryDataSummaryFunctionalLocationFilters
                    : queryDataSummaryFilters
                ).reduce((acc: IGetSummaryReportDashboardFilters["shift"], obj) => {
                    const shiftId = obj.shift.shift_id;
                    const existingShiftIndex = acc.findIndex((summary) => summary.shift_id === shiftId);

                    if (!(existingShiftIndex >= 0)) {
                        acc = [
                            ...acc,
                            {
                                shift_id: shiftId,
                                name: obj.shift.name,
                                machine_centers: [
                                    {
                                        machine_center_id: obj.machine_center.machine_center_id,
                                    },
                                ],
                                functional_locations: obj.functional_location
                                    ? [{ functional_location_id: obj.functional_location.functional_location_id }]
                                    : [],
                            },
                        ];
                    } else {
                        const existingMachineCenterIndex = acc[existingShiftIndex].machine_centers.findIndex(
                            (summary) => summary.machine_center_id === obj.machine_center.machine_center_id,
                        );
                        const existingFunctionalLocationIndex = acc[existingShiftIndex].functional_locations.findIndex(
                            (summary) =>
                                summary.functional_location_id === obj.functional_location?.functional_location_id,
                        );

                        if (!(existingMachineCenterIndex >= 0)) {
                            acc[existingShiftIndex] = {
                                ...acc[existingShiftIndex],
                                machine_centers: [
                                    ...acc[existingShiftIndex].machine_centers,
                                    {
                                        machine_center_id: obj.machine_center.machine_center_id,
                                    },
                                ],
                            };
                        }
                        if (obj.functional_location && !(existingFunctionalLocationIndex >= 0)) {
                            acc[existingShiftIndex] = {
                                ...acc[existingShiftIndex],
                                functional_locations: [
                                    ...acc[existingShiftIndex].functional_locations,
                                    { functional_location_id: obj.functional_location.functional_location_id },
                                ],
                            };
                        }
                    }
                    return acc;
                }, []),
            } as IGetSummaryReportDashboardFilters;

            return summaryFilters;
        }, [queryDataSummaryFilters, queryDataSummaryFunctionalLocationFilters, filters?.functionalLocation]);

        return {
            loading,
            summaryReportDashboardFiltersData: filtersData,
            error,
        };
    },
    GetDetailsFilters: (mill_id: number, filters: IDetailsReportFilters) => {
        const startDateRange = getStartOfDateRange(filters.startDate as Date);
        const endDateRange = getEndOfDateRange(filters.endDate as Date);

        const [filtersData, setFiltersData] = useState<IDetailsFilters>({
            failure_code: [],
            failure_type: [],
            shift: [],
            machine_center: [],
            production_line: [],
            functional_location: [],
            user: [],
        });

        const noEndOfShiftFilter = {
            _or: [
                {
                    failure_code: {
                        _or: [
                            {
                                code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                            },
                            {
                                code: { _is_null: true },
                            },
                        ],
                    },
                },
                {
                    failure_code_id: { _is_null: true },
                },
            ],
        };

        const { loading, error } = useQuery(getDetailsFilters, {
            onCompleted: (data) => {
                if (data?.event_report_filters && data?.shift_filters) {
                    const queryData: IDetailsFiltersQuery[] = data.event_report_filters;
                    const queryShiftData: IDetailsShiftFiltersQuery[] = data?.shift_filters;

                    const porductionLineParsed: IDetailsFilters["production_line"] = queryData.reduce(
                        (acc: IDetailsFilters["production_line"], obj) => {
                            const productionLineId = obj.machine_center.production_line.production_line_id;
                            const existingProductionLineIndex = acc.findIndex(
                                (summary) => summary.production_line_id === productionLineId,
                            );

                            if (!(existingProductionLineIndex >= 0)) {
                                acc = [
                                    ...acc,
                                    {
                                        name: obj.machine_center.production_line.name,
                                        production_line_id: obj.machine_center.production_line.production_line_id,
                                    },
                                ];
                            }
                            return acc;
                        },
                        [],
                    );

                    const machineCenterParsed: IDetailsFilters["machine_center"] = queryData.reduce(
                        (acc: IDetailsFilters["machine_center"], obj) => {
                            const machineCenterId = obj.machine_center.machine_center_id;
                            const existingMachineCenterIndex = acc.findIndex(
                                (summary) => summary.machine_center_id === machineCenterId,
                            );

                            if (!(existingMachineCenterIndex >= 0)) {
                                acc = [
                                    ...acc,
                                    {
                                        machine_center_id: obj.machine_center.machine_center_id,
                                        name: obj.machine_center.name,
                                        production_line_id: obj.machine_center.production_line.production_line_id,
                                    },
                                ];
                            }
                            return acc;
                        },
                        [],
                    );

                    const functionalLocationParsed: IDetailsFilters["functional_location"] = queryData.reduce(
                        (acc: IDetailsFilters["functional_location"], obj) => {
                            const functionalLocationId = obj.functional_location?.functional_location_id;
                            const existingFunctionalLocationIndex = acc.findIndex(
                                (summary) => summary.functional_location_id === functionalLocationId,
                            );

                            if (obj.functional_location && !(existingFunctionalLocationIndex >= 0)) {
                                acc = [
                                    ...acc,
                                    {
                                        translation: obj.functional_location.translation,
                                        functional_location_id: obj.functional_location.functional_location_id,
                                        area: {
                                            machine_center: {
                                                machine_center_id: obj.machine_center.machine_center_id,
                                                production_line_id:
                                                    obj.machine_center.production_line.production_line_id,
                                            },
                                        },
                                    },
                                ];
                            }
                            return acc;
                        },
                        [],
                    );

                    const shiftsParsed: IDetailsFilters["shift"] = queryShiftData.reduce(
                        (acc: IDetailsFilters["shift"], shiftData) => {
                            if (
                                porductionLineParsed.some(
                                    (pl) => pl.production_line_id === shiftData.production_line_id,
                                )
                            ) {
                                const existingShift = acc.find((s) => s.shift_id === shiftData.shift.shift_id);

                                if (existingShift) {
                                    existingShift.production_line_ids.push(shiftData.production_line_id);
                                    existingShift.machine_centers.push(
                                        ...machineCenterParsed
                                            .filter((mc) => mc.production_line_id === shiftData.production_line_id)
                                            .map((mc) => ({ machine_center: mc })),
                                    );
                                    existingShift.functional_locations.push(
                                        ...functionalLocationParsed
                                            .filter(
                                                (fl) =>
                                                    fl.area.machine_center.production_line_id ===
                                                    shiftData.production_line_id,
                                            )
                                            .map((fl) => ({ functional_location_id: fl.functional_location_id })),
                                    );
                                } else {
                                    acc.push({
                                        shift_id: shiftData.shift.shift_id,
                                        name: shiftData.shift.name,
                                        production_line_ids: [shiftData.production_line_id],
                                        machine_centers: machineCenterParsed
                                            .filter((mc) => mc.production_line_id === shiftData.production_line_id)
                                            .map((mc) => ({ machine_center: mc })),
                                        functional_locations: functionalLocationParsed
                                            .filter(
                                                (fl) =>
                                                    fl.area.machine_center.production_line_id ===
                                                    shiftData.production_line_id,
                                            )
                                            .map((fl) => ({ functional_location_id: fl.functional_location_id })),
                                    });
                                }
                            }
                            return acc;
                        },
                        [],
                    );

                    const queryDataWithShifts: (IDetailsFiltersQuery & { shifts: number[] })[] = queryData.map(
                        (el) => ({
                            ...el,
                            shifts: shiftsParsed
                                .filter((shift) =>
                                    shift.production_line_ids.includes(
                                        el.machine_center.production_line.production_line_id,
                                    ),
                                )
                                .map((el) => el.shift_id),
                        }),
                    );

                    const usersParsed: IDetailsFilters["user"] = queryDataWithShifts.reduce(
                        (acc: IDetailsFilters["user"], obj) => {
                            const userId = obj.user_operator.user_id;
                            const existingUserIndex = acc.findIndex((summary) => summary.user_id === userId);

                            if (!(existingUserIndex >= 0)) {
                                acc = [
                                    ...acc,
                                    {
                                        user_id: userId,
                                        first_name: obj.user_operator.first_name,
                                        last_name: obj.user_operator.last_name,
                                        machine_centers: [
                                            {
                                                machine_center: {
                                                    machine_center_id: obj.machine_center.machine_center_id,
                                                    production_line_id:
                                                        obj.machine_center.production_line.production_line_id,
                                                },
                                            },
                                        ],
                                        functional_locations: obj.functional_location
                                            ? [
                                                  {
                                                      functional_location_id:
                                                          obj.functional_location.functional_location_id,
                                                  },
                                              ]
                                            : [],
                                        shifts: obj.shifts.map((id) => ({
                                            shift_day: {
                                                shift_id: id,
                                            },
                                        })),
                                    },
                                ];
                            } else {
                                const existingMachineCenterIndex = acc[existingUserIndex].machine_centers.findIndex(
                                    (summary) =>
                                        summary.machine_center.machine_center_id ===
                                        obj.machine_center.machine_center_id,
                                );
                                const existingFunctionalLocationIndex = acc[
                                    existingUserIndex
                                ].functional_locations.findIndex(
                                    (summary) =>
                                        summary.functional_location_id ===
                                        obj.functional_location?.functional_location_id,
                                );
                                const newShifts = obj.shifts.filter(
                                    (id) =>
                                        !acc[existingUserIndex].shifts.some((shift) => shift.shift_day.shift_id === id),
                                );

                                if (!(existingMachineCenterIndex >= 0)) {
                                    acc[existingUserIndex] = {
                                        ...acc[existingUserIndex],
                                        machine_centers: [
                                            ...acc[existingUserIndex].machine_centers,
                                            {
                                                machine_center: {
                                                    machine_center_id: obj.machine_center.machine_center_id,
                                                    production_line_id:
                                                        obj.machine_center.production_line.production_line_id,
                                                },
                                            },
                                        ],
                                    };
                                }
                                if (obj.functional_location && !(existingFunctionalLocationIndex >= 0)) {
                                    acc[existingUserIndex] = {
                                        ...acc[existingUserIndex],
                                        functional_locations: [
                                            ...acc[existingUserIndex].functional_locations,
                                            { functional_location_id: obj.functional_location.functional_location_id },
                                        ],
                                    };
                                }
                                if (!!newShifts.length) {
                                    acc[existingUserIndex] = {
                                        ...acc[existingUserIndex],
                                        shifts: [
                                            ...acc[existingUserIndex].shifts,
                                            ...newShifts.map((id) => ({
                                                shift_day: {
                                                    shift_id: id,
                                                },
                                            })),
                                        ],
                                    };
                                }
                            }
                            return acc;
                        },
                        [],
                    );

                    const failureCodeParsed: IDetailsFilters["failure_code"] = queryDataWithShifts.reduce(
                        (acc: IDetailsFilters["failure_code"], obj) => {
                            const failureCodeId = obj.failure_code?.failure_code_id;
                            const existingFailureCodeIndex = acc.findIndex(
                                (summary) => summary.failure_code_id === failureCodeId,
                            );

                            if (obj.failure_code && !(existingFailureCodeIndex >= 0)) {
                                acc = [
                                    ...acc,
                                    {
                                        name: obj.failure_code.name,
                                        failure_code_id: obj.failure_code.failure_code_id,
                                        failure_type_id: obj.failure_type.failure_type_id,
                                        machine_centers: [
                                            {
                                                machine_center: {
                                                    machine_center_id: obj.machine_center.machine_center_id,
                                                    production_line_id:
                                                        obj.machine_center.production_line.production_line_id,
                                                },
                                            },
                                        ],
                                        functional_locations: obj.functional_location
                                            ? [
                                                  {
                                                      functional_location_id:
                                                          obj.functional_location.functional_location_id,
                                                  },
                                              ]
                                            : [],
                                        shifts: obj.shifts.map((id) => ({
                                            shift_day: {
                                                shift_id: id,
                                            },
                                        })),
                                        operators: [{ user_id: obj.user_operator.user_id }],
                                    },
                                ];
                            } else if (obj.failure_code && existingFailureCodeIndex >= 0) {
                                const existingMachineCenterIndex = acc[
                                    existingFailureCodeIndex
                                ].machine_centers.findIndex(
                                    (summary) =>
                                        summary.machine_center.machine_center_id ===
                                        obj.machine_center.machine_center_id,
                                );
                                const existingFunctionalLocationIndex = acc[
                                    existingFailureCodeIndex
                                ].functional_locations.findIndex(
                                    (summary) =>
                                        summary.functional_location_id ===
                                        obj.functional_location?.functional_location_id,
                                );
                                const newShifts = obj.shifts.filter(
                                    (id) =>
                                        !acc[existingFailureCodeIndex].shifts.some(
                                            (shift) => shift.shift_day.shift_id === id,
                                        ),
                                );
                                const existingOperatorsIndex = acc[existingFailureCodeIndex].operators.findIndex(
                                    (summary) => summary.user_id === obj.user_operator.user_id,
                                );

                                if (!(existingMachineCenterIndex >= 0)) {
                                    acc[existingFailureCodeIndex] = {
                                        ...acc[existingFailureCodeIndex],
                                        machine_centers: [
                                            ...acc[existingFailureCodeIndex].machine_centers,
                                            {
                                                machine_center: {
                                                    machine_center_id: obj.machine_center.machine_center_id,
                                                    production_line_id:
                                                        obj.machine_center.production_line.production_line_id,
                                                },
                                            },
                                        ],
                                    };
                                }
                                if (obj.functional_location && !(existingFunctionalLocationIndex >= 0)) {
                                    acc[existingFailureCodeIndex] = {
                                        ...acc[existingFailureCodeIndex],
                                        functional_locations: [
                                            ...acc[existingFailureCodeIndex].functional_locations,
                                            { functional_location_id: obj.functional_location.functional_location_id },
                                        ],
                                    };
                                }
                                if (!!newShifts.length) {
                                    acc[existingFailureCodeIndex] = {
                                        ...acc[existingFailureCodeIndex],
                                        shifts: [
                                            ...acc[existingFailureCodeIndex].shifts,
                                            ...newShifts.map((id) => ({
                                                shift_day: {
                                                    shift_id: id,
                                                },
                                            })),
                                        ],
                                    };
                                }
                                if (!(existingOperatorsIndex >= 0)) {
                                    acc[existingFailureCodeIndex] = {
                                        ...acc[existingFailureCodeIndex],
                                        operators: [
                                            ...acc[existingFailureCodeIndex].operators,
                                            { user_id: obj.user_operator.user_id },
                                        ],
                                    };
                                }
                            }
                            return acc;
                        },
                        [],
                    );

                    const failureTypeParsed: IDetailsFilters["failure_type"] = queryDataWithShifts.reduce(
                        (acc: IDetailsFilters["failure_type"], obj) => {
                            const failureTypeId = obj.failure_type.failure_type_id;
                            const existingFailureTypeIndex = acc.findIndex(
                                (summary) => summary.failure_type_id === failureTypeId,
                            );

                            if (!(existingFailureTypeIndex >= 0)) {
                                acc = [
                                    ...acc,
                                    {
                                        failure_type_id: obj.failure_type.failure_type_id,
                                        name: obj.failure_type.name,
                                        machine_centers: [
                                            {
                                                machine_center: {
                                                    machine_center_id: obj.machine_center.machine_center_id,
                                                    production_line_id:
                                                        obj.machine_center.production_line.production_line_id,
                                                },
                                            },
                                        ],
                                        functional_locations: obj.functional_location
                                            ? [
                                                  {
                                                      functional_location_id:
                                                          obj.functional_location.functional_location_id,
                                                  },
                                              ]
                                            : [],
                                        shifts: obj.shifts.map((id) => ({
                                            shift_day: {
                                                shift_id: id,
                                            },
                                        })),
                                        operators: [{ user_id: obj.user_operator.user_id }],
                                    },
                                ];
                            } else {
                                const existingMachineCenterIndex = acc[
                                    existingFailureTypeIndex
                                ].machine_centers.findIndex(
                                    (summary) =>
                                        summary.machine_center.machine_center_id ===
                                        obj.machine_center.machine_center_id,
                                );
                                const existingFunctionalLocationIndex = acc[
                                    existingFailureTypeIndex
                                ].functional_locations.findIndex(
                                    (summary) =>
                                        summary.functional_location_id ===
                                        obj.functional_location?.functional_location_id,
                                );
                                const newShifts = obj.shifts.filter(
                                    (id) =>
                                        !acc[existingFailureTypeIndex].shifts.some(
                                            (shift) => shift.shift_day.shift_id === id,
                                        ),
                                );
                                const existingOperatorsIndex = acc[existingFailureTypeIndex].operators.findIndex(
                                    (summary) => summary.user_id === obj.user_operator.user_id,
                                );

                                if (!(existingMachineCenterIndex >= 0)) {
                                    acc[existingFailureTypeIndex] = {
                                        ...acc[existingFailureTypeIndex],
                                        machine_centers: [
                                            ...acc[existingFailureTypeIndex].machine_centers,
                                            {
                                                machine_center: {
                                                    machine_center_id: obj.machine_center.machine_center_id,
                                                    production_line_id:
                                                        obj.machine_center.production_line.production_line_id,
                                                },
                                            },
                                        ],
                                    };
                                }
                                if (obj.functional_location && !(existingFunctionalLocationIndex >= 0)) {
                                    acc[existingFailureTypeIndex] = {
                                        ...acc[existingFailureTypeIndex],
                                        functional_locations: [
                                            ...acc[existingFailureTypeIndex].functional_locations,
                                            { functional_location_id: obj.functional_location.functional_location_id },
                                        ],
                                    };
                                }
                                if (!!newShifts.length) {
                                    acc[existingFailureTypeIndex] = {
                                        ...acc[existingFailureTypeIndex],
                                        shifts: [
                                            ...acc[existingFailureTypeIndex].shifts,
                                            ...newShifts.map((id) => ({
                                                shift_day: {
                                                    shift_id: id,
                                                },
                                            })),
                                        ],
                                    };
                                }
                                if (!(existingOperatorsIndex >= 0)) {
                                    acc[existingFailureTypeIndex] = {
                                        ...acc[existingFailureTypeIndex],
                                        operators: [
                                            ...acc[existingFailureTypeIndex].operators,
                                            { user_id: obj.user_operator.user_id },
                                        ],
                                    };
                                }
                            }
                            return acc;
                        },
                        [],
                    );

                    const filtersData: IDetailsFilters = {
                        failure_type: failureTypeParsed,
                        machine_center: machineCenterParsed,
                        production_line: porductionLineParsed,
                        failure_code: failureCodeParsed,
                        functional_location: functionalLocationParsed,
                        user: usersParsed,
                        shift: shiftsParsed,
                    };

                    setFiltersData(filtersData);
                }
            },
            variables: {
                where: {
                    ...noEndOfShiftFilter,
                    event_report_downtime_details: {
                        _or: [
                            {
                                shift_date: {
                                    _gte: startDateRange,
                                    _lte: endDateRange,
                                },
                            },
                            {
                                end_date: { _is_null: true },
                                shift_date: { _lte: endDateRange },
                            },
                        ],
                    },
                    machine_center: {
                        production_line: {
                            mill_id: { _eq: mill_id },
                        },
                    },
                    measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                    ignore_event: { _eq: false },
                },
                whereShift: {
                    _or: [
                        {
                            disabled_at: {
                                _gte: startDateRange,
                            },
                        },
                        { disabled_at: { _is_null: true } },
                    ],
                    production_line: {
                        mill_id: { _eq: mill_id },
                    },
                },
            },
        });

        return { loading, detailsFiltersData: filtersData, error };
    },
    GetCorporateDashboardFilters: (filters: ICorporateDashboardFilters) => {
        const createdAtFilter = {
            created_at: {
                _gte: getStartOfDateRange(filters.startDate as Date),
                _lte: getEndOfDateRange(filters.endDate as Date),
            },
        };
        const noEndOfShiftFilter = {
            _or: [
                {
                    failure_code: {
                        _or: [
                            {
                                code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                            },
                            {
                                code: { _is_null: true },
                            },
                        ],
                    },
                },
                {
                    failure_code_id: { _is_null: true },
                },
            ],
        };
        const { loading, error, data } = useQuery(getCorporateDashboardFilters, {
            variables: {
                where: {
                    ...noEndOfShiftFilter,
                    ...createdAtFilter,
                    measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                },
            },
        });

        const queryData: ICorporateDashboardFiltersDataQuery[] = useMemo(
            () => data?.event_failure_code_summary_per_mc || [],
            [data?.event_failure_code_summary_per_mc],
        );

        const filtersData = useMemo(() => {
            const summaryCorporateFilters: ICorporateDashboardFiltersData = {
                division: queryData.reduce((acc: ICorporateDashboardFiltersData["division"], obj) => {
                    const divisionId = obj.machine_center.production_line.mill.division?.division_id;
                    const existingDivisionIndex = acc.findIndex((summary) => summary.division_id === divisionId);

                    if (obj.machine_center.production_line.mill.division && !(existingDivisionIndex >= 0)) {
                        acc = [
                            ...acc,
                            {
                                division_id: obj.machine_center.production_line.mill.division.division_id,
                                name: obj.machine_center.production_line.mill.division.name,
                            },
                        ];
                    }

                    return acc;
                }, []),
                region: queryData.reduce((acc: ICorporateDashboardFiltersData["region"], obj) => {
                    const regionId = obj.machine_center.production_line.mill.region?.region_id;
                    const existingRegionIndex = acc.findIndex((summary) => summary.region_id === regionId);

                    if (obj.machine_center.production_line.mill.region && !(existingRegionIndex >= 0)) {
                        acc = [
                            ...acc,
                            {
                                region_id: obj.machine_center.production_line.mill.region.region_id,
                                name: obj.machine_center.production_line.mill.region.name,
                                division: {
                                    division_id: obj.machine_center.production_line.mill.region.division.division_id,
                                    name: obj.machine_center.production_line.mill.region.division.name,
                                },
                            },
                        ];
                    }

                    return acc;
                }, []),
                machine_center: queryData.reduce((acc: ICorporateDashboardFiltersData["machine_center"], obj) => {
                    const machineCenterId = obj.machine_center.machine_center_id;
                    const existingMachineCenterIndex = acc.findIndex(
                        (summary) => summary.machine_center_id === machineCenterId,
                    );

                    if (!(existingMachineCenterIndex >= 0)) {
                        acc = [
                            ...acc,
                            {
                                machine_center_id: machineCenterId,
                                name: obj.machine_center.name,
                                production_line: {
                                    production_line_id: obj.machine_center.production_line.production_line_id,
                                    name: obj.machine_center.production_line.name,
                                    mill: {
                                        name: obj.machine_center.production_line.mill.name,
                                        division: obj.machine_center.production_line.mill.division
                                            ? {
                                                  division_id:
                                                      obj.machine_center.production_line.mill.division.division_id,
                                                  name: obj.machine_center.production_line.mill.division.name,
                                              }
                                            : null,
                                        region: obj.machine_center.production_line.mill.region
                                            ? {
                                                  region_id: obj.machine_center.production_line.mill.region.region_id,
                                                  name: obj.machine_center.production_line.mill.region.name,
                                              }
                                            : null,
                                    },
                                },
                            },
                        ];
                    }

                    return acc;
                }, []),
                production_line: queryData.reduce((acc: ICorporateDashboardFiltersData["production_line"], obj) => {
                    const productionLineId = obj.machine_center.production_line.production_line_id;
                    const existingProductionLineIndex = acc.findIndex(
                        (summary) => summary.production_line_id === productionLineId,
                    );

                    if (!(existingProductionLineIndex >= 0)) {
                        acc = [
                            ...acc,
                            {
                                production_line_id: productionLineId,
                                name: obj.machine_center.production_line.name,
                                mill: {
                                    name: obj.machine_center.production_line.mill.name,
                                    division: obj.machine_center.production_line.mill.division
                                        ? {
                                              division_id: obj.machine_center.production_line.mill.division.division_id,
                                              name: obj.machine_center.production_line.mill.division.name,
                                          }
                                        : null,
                                    region: obj.machine_center.production_line.mill.region
                                        ? {
                                              region_id: obj.machine_center.production_line.mill.region.region_id,
                                              name: obj.machine_center.production_line.mill.region.name,
                                          }
                                        : null,
                                },
                            },
                        ];
                    }

                    return acc;
                }, []),
            } as ICorporateDashboardFiltersData;

            return summaryCorporateFilters;
        }, [queryData]);

        return { loading, corporateDashboardFiltersData: filtersData, error };
    },
};
